import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  DialogContentText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { listClientWorkflowsAsync, cancelSubscriptionAsync } from '../infrastructure/httpclient';
import { useSnackbar } from 'notistack';
import { availableWorkflows } from '../infrastructure/workflows';

const WorkflowsPage = () => {
  const { user, token } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [unsubscribeOpen, setUnsubscribeOpen] = useState(false);
  const [isUnsubscribing, setIsUnsubscribing] = useState(false);
  const [workflowToUnsubscribe, setWorkflowToUnsubscribe] = useState(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [workflows, setWorkflows] = useState(
    availableWorkflows.map((w) => ({ ...w, checked: false }))
  );
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (user?.email) {
      listClientWorkflowsAsync(token)
        .then((response) => {
          const clientWorkflows = response.data;
          setWorkflows((prevWorkflows) =>
            prevWorkflows.map((workflow) => ({
              ...workflow,
              checked: clientWorkflows.some(
                (cw) => cw.workflow_name === workflow.workflow_name && cw.workflow_enabled
              ),
            }))
          );
        })
        .catch((error) => {
          console.error("Failed to fetch client workflows:", error);
          enqueueSnackbar('Failed to load workflows', { variant: 'error' });
        });
    }
  }, [user, token, enqueueSnackbar, location.state]);

  const handleOpen = (workflow) => {
    setSelectedWorkflow(workflow);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedWorkflow(null);
  };

  const handleSubscribeToggle = (workflow) => {
    if (workflow.checked) {
      setWorkflowToUnsubscribe(workflow);
      setUnsubscribeOpen(true);
    } else {
      navigate(`/subscription/${workflow.id}`);
    }
  };

  const handleUnsubscribe = async () => {
    if (!workflowToUnsubscribe) return;

    setIsUnsubscribing(true);
    try {
      await cancelSubscriptionAsync(token, {
        workflow_name: workflowToUnsubscribe.workflow_name,
      });

      setWorkflows((prevWorkflows) =>
        prevWorkflows.map((w) =>
          w.id === workflowToUnsubscribe.id ? { ...w, checked: false } : w
        )
      );
      enqueueSnackbar('Unsubscribed successfully!', { variant: 'success' });
    } catch (error) {
      console.error("Failed to unsubscribe:", error);
      enqueueSnackbar('Failed to unsubscribe. Please try again.', { variant: 'error' });
    } finally {
      setIsUnsubscribing(false);
      setUnsubscribeOpen(false);
      setWorkflowToUnsubscribe(null);
    }
  };


  if (!user) {
    return (
      <Container sx={{ mt: 4 }}>
        <Typography variant="h6">
          You must be logged in to view this page.
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 } }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Available Workflows
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ p: { xs: 1, sm: 2 } }}>Name</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, p: { xs: 1, sm: 2 } }}>Problem it solves</TableCell>
                <TableCell sx={{ p: { xs: 1, sm: 2 } }}></TableCell>
                <TableCell sx={{ p: { xs: 1, sm: 2 } }}>Details</TableCell>
                <TableCell sx={{ p: { xs: 1, sm: 2 } }}>Subscribe</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {workflows.map((workflow) => (
                <TableRow key={workflow.id}>
                  <TableCell sx={{ p: { xs: 1, sm: 2 }, wordBreak: 'break-word' }}>{workflow.name}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, p: { xs: 1, sm: 2 }, wordBreak: 'break-word' }}>{workflow.problem}</TableCell>
                  <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                    <Button
                      sx={{
                        p: { xs: 0.5, sm: 1 },
                        minWidth: { xs: 'auto', sm: '64px' },
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      }}
                      variant="outlined"
                      onClick={() => navigate(`/workflow-runs/${workflow.workflow_name}`)}
                      disabled={!workflow.checked || workflow.scheduled_trigger === false}
                    >
                      Runs
                    </Button>
                  </TableCell>
                  <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                    <Button
                      sx={{
                        p: { xs: 0.5, sm: 1 },
                        minWidth: { xs: 'auto', sm: '64px' },
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      }}
                      variant="outlined" onClick={() => handleOpen(workflow)}>
                      View Details
                    </Button>
                  </TableCell>
                  <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                    <Switch
                      checked={workflow.checked}
                      onChange={() => handleSubscribeToggle(workflow)}
                      inputProps={{ 'aria-label': 'controlled' }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {selectedWorkflow && (
        <Dialog open={open} onClose={handleClose}>
          <DialogTitle>{selectedWorkflow.name}</DialogTitle>
          <DialogContent>
            {selectedWorkflow.details.split('\n\n').map((paragraph, index) => {
              if (index === 0) {
                return <Typography key={index} sx={{ whiteSpace: 'pre-line' }} gutterBottom>{paragraph}</Typography>;
              }

              const parts = paragraph.split('\n');
              const title = parts[0];
              const description = parts.slice(1).join('\n');

              return (
                <div key={index}>
                  <Typography variant="h6" component="h3" sx={{ mt: 2, fontWeight: 'bold' }} gutterBottom>
                    {title}
                  </Typography>
                  <Typography sx={{ whiteSpace: 'pre-line' }} gutterBottom>
                    {description}
                  </Typography>
                </div>
              );
            })}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {workflowToUnsubscribe && (
        <Dialog open={unsubscribeOpen} onClose={() => setUnsubscribeOpen(false)}>
          <DialogTitle>Unsubscribe</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to unsubscribe from {workflowToUnsubscribe.name}?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUnsubscribeOpen(false)} disabled={isUnsubscribing}>Cancel</Button>
            <Button onClick={handleUnsubscribe} color="primary" disabled={isUnsubscribing}>
              {isUnsubscribing ? 'Processing...' : 'Unsubscribe'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Container>
  );
};

export default WorkflowsPage;
